import api from "../../../../api";
import React, { useEffect, useState } from "react";
import values from "../../../../values";
import Context from "../../../../context";

import * as Yup from "yup";

import { useFormik } from "formik";
import { useQuery } from "react-query";
import {
  ICityItem,
  ICreateAndUpdateMunicipality,
  IProvinceItem,
} from "../../../../types/common.interface";
import { ICreateAndUpdateSuperAgent } from "../../../../types/superAgent.interface";
import {
  CustomButton2,
  CustomDropdownSearch,
  CustomInput,
  CustomModal,
  CustomView,
} from "../../../../components";

import "./index.sass";
import TitleLine from "../../../../components/TitleLine";
import validations from "../../../../validations";
import utils from "../../../../utils";
import { useTranslation } from "react-i18next";

const Update = () => {
  // ** ===========================================================================================================
  // **                                            Value Validation
  // ** -----------------------------------------------------------------------------------------------------------
  const {
    showModal,
    setShowModal,
    updateFunction,
    isLoading,
    selectedSuperAgent,
  } = React.useContext(Context.MunicipulityScreen);
  const [selectedCity, setSelectedCity] = React.useState<ICityItem>();
  const [selectedProvince, setSelectedProvince] =
    React.useState<IProvinceItem>();
  const [selectedFinancialGroup, setSelectedFinancialGroup] =
    React.useState<any>(undefined);

  const [dropDownErrors, setDropDownErrors] = useState<any>({
    province: "",
    city: "",
    financialGroup: "",
  });

  // !! ===========================================================================================================
  // !!                                            Start API Call
  // !! -----------------------------------------------------------------------------------------------------------
  const getAllProvince = useQuery([values.useQueryKeys.getAllProvince], () =>
    api.get.common.getAllProvince()
  );
  const getAllCity = useQuery(
    [values.useQueryKeys.getAllCity, selectedProvince, selectedSuperAgent],
    () =>
      api.get.common.getAllCity({
        provinceCode: selectedProvince
          ? selectedProvince?.Code
          : selectedSuperAgent?.superAgentSchoolInformation?.province?.Code,
      })
  );
  const getAllFinancilalGroup = useQuery(
    [values.useQueryKeys?.getAllFinancialGroup],
    () => api.post.administrator.getAllFinancialGroup({})
  );

  // !! -----------------------------------------------------------------------------------------------------------
  // !!                                             End API Call
  // !! ===========================================================================================================

  const { t } = useTranslation();

  // ?? ===========================================================================================================
  // ??                                           Use Formik Function
  // ?? -----------------------------------------------------------------------------------------------------------

  useEffect(() => {
    setSelectedProvince(selectedSuperAgent?.municipalityInformation?.province);
    setSelectedCity(selectedSuperAgent?.municipalityInformation?.city);
    setSelectedFinancialGroup(
      selectedSuperAgent?.municipalityInformation?.financialGroupSchool
    );
  }, [selectedSuperAgent]);

  const formik = useFormik<ICreateAndUpdateMunicipality>({
    enableReinitialize: true,
    initialValues: {
      _id: selectedSuperAgent?._id ? selectedSuperAgent._id : "",
      name: selectedSuperAgent?.municipalityInformation?.name
        ? selectedSuperAgent?.municipalityInformation.name
        : "",
      phoneNumber: selectedSuperAgent?.phoneNumber
        ? selectedSuperAgent.phoneNumber
        : "",
    },
    validationSchema: Yup.object({
      // validation
      name: validations.yupValidations.firstName,
      phoneNumber: validations.yupValidations.phoneNumber,
    }),
    onSubmit: (values: ICreateAndUpdateMunicipality, { setSubmitting }) => {
      // if (!selectedProvince) return utils.errorHandler(9011);
      // if (!selectedCity) return utils.errorHandler(9012);
      const formValues: ICreateAndUpdateMunicipality = {
        ...values,
        city: selectedCity,
        province: selectedProvince,
        financialGroupSchool: selectedFinancialGroup,
      };
      if (selectedProvince && selectedCity && selectedFinancialGroup) {
        Promise.resolve(updateFunction(formValues))
          .then(() => {
            setSelectedCity(undefined);
            setSelectedProvince(undefined);
            setSelectedFinancialGroup(undefined);
          })
          .catch((e) => setSubmitting(false));
      }
    },
  });

  function submitHandler() {
    // validation

    let errors = {};
    if (!selectedProvince) {
      errors = {
        ...errors,
        province: t("province_is_required"),
        city: t("city_is_required"),
      };
    } else if (!selectedCity) {
      errors = {
        ...errors,
        city: t("city_is_required"),
      };
    }
    if (!selectedFinancialGroup) {
      errors = {
        ...errors,
        financialGroup: t("financial_group_is_required"),
      };
    }

    setDropDownErrors({
      ...errors,
    });

    formik.submitForm();
  }

  function closeHandler() {
    setShowModal("NONE");
  }

  // ?? ===========================================================================================================
  // ??                                               JSX Content
  // ?? -----------------------------------------------------------------------------------------------------------

  return (
    <section id="super-agent-update-modal-container">
      <form onSubmit={submitHandler}>
        <CustomModal
          onClose={() => setShowModal("NONE")}
          open={showModal === "UPDATE"}
        >
          <CustomView.Container className="create-form-content">
            <CustomView.Row className="form-row">
              <CustomView.Column medium={6} className="form-colum dropdown">
                <label className="form-label">{t("province")}</label>
                <CustomDropdownSearch
                  items={getAllProvince?.data?.result}
                  itemName="Name"
                  defaultItem={selectedProvince}
                  onSelectItem={(event: IProvinceItem) => {
                    setSelectedCity(undefined);
                    setSelectedProvince(event);
                    setDropDownErrors({ ...dropDownErrors, province: "" });
                  }}
                  placeHolder={t("selectProvince")}
                  isLoading={getAllProvince.isLoading}
                  isSearch
                />
                <div className="error">
                  {dropDownErrors.province !== "" && dropDownErrors.province}
                </div>
              </CustomView.Column>
              <CustomView.Column medium={6} className="form-colum dropdown">
                <label className="form-label">{t("city")}</label>
                <CustomDropdownSearch
                  items={getAllCity?.data?.result}
                  itemName="Name"
                  defaultItem={selectedCity}
                  onSelectItem={(event: ICityItem) => {
                    setSelectedCity(event);
                    setDropDownErrors({ ...dropDownErrors, city: "" });
                  }}
                  placeHolder={t("selectCity")}
                  isLoading={getAllCity.isLoading}
                  isSearch
                />
                <div className="error">
                  {dropDownErrors.city !== "" && dropDownErrors.city}
                </div>
              </CustomView.Column>
            </CustomView.Row>
            <CustomView.Row className="form-row">
              <CustomView.Column medium={6} className="form-colum">
                <CustomInput
                  name="name"
                  id="name"
                  label={t("municipalityName")}
                  value={formik.values.name}
                  onValueChange={formik.handleChange}
                  formikProps={{ onBlur: formik.handleBlur }}
                  error={formik.errors.name && formik.touched.name}
                  errorText={formik.errors.name}
                />
              </CustomView.Column>
              <CustomView.Column medium={6} className="form-colum">
                <CustomInput
                  name="phoneNumber"
                  id="phoneNumber"
                  label={t("phoneNumber")}
                  value={utils.convertNumbers2English(
                    formik.values.phoneNumber as string
                  )}
                  onValueChange={formik.handleChange}
                  formikProps={{ onBlur: formik.handleBlur }}
                  error={
                    formik.errors.phoneNumber && formik.touched.phoneNumber
                  }
                  errorText={formik.errors.phoneNumber}
                  maxLength={11}
                />
              </CustomView.Column>
              <CustomView.Column medium={6} className="form-colum dropdown">
                <label className="form-label">{t("financialGroup")}</label>
                <CustomDropdownSearch
                  items={getAllFinancilalGroup?.data?.result}
                  itemName="name"
                  defaultItem={selectedFinancialGroup}
                  onSelectItem={(event: any) => {
                    setSelectedFinancialGroup(event);
                    setDropDownErrors({
                      ...dropDownErrors,
                      financialGroup: "",
                    });
                  }}
                  placeHolder={t("selectFinancialGroup")}
                />
                <div className="error">
                  {dropDownErrors.financialGroup !== "" &&
                    dropDownErrors.financialGroup}
                </div>
              </CustomView.Column>
              <CustomView.Column medium={6} className="form-colum dropdown">
                {" "}
              </CustomView.Column>
            </CustomView.Row>

            <CustomView.Row className="form-row buttons-group">
              <CustomView.Column
                large={3}
                medium={4}
                small={6}
                className="form-colum"
              >
                <CustomButton2
                  isLoading={isLoading}
                  onClick={closeHandler}
                  title={t("cancel")}
                  type="danger"
                />
              </CustomView.Column>
              <CustomView.Column
                large={3}
                medium={4}
                small={6}
                className="form-colum"
              >
                <CustomButton2
                  isLoading={isLoading}
                  onClick={submitHandler}
                  title={t("update")}
                />
              </CustomView.Column>
            </CustomView.Row>
          </CustomView.Container>
        </CustomModal>
      </form>
    </section>
  );
};

export default Update;
